import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Link,
	TextField,
	Typography,
} from '@mui/material';
import { FC, FormEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addShopifyAppRequest } from 'redux/reducers/shopifyApps/reducer';
import { getAddShopifyAppLoader } from 'redux/reducers/shopifyApps/selectors';
import { COLORS } from 'services/constants/colors';

const CREATE_APP_HELP_URL =
	'https://help.easproject.com/hc/en-gb/articles/6473304471711-Creating-the-EAS-App-in-Shopify';

type TAddDevAppDialogProps = {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
};

type TFormValues = {
	name: string;
	apiKey: string;
	apiSecret: string;
};

const EMPTY_VALUES: TFormValues = {
	name: '',
	apiKey: '',
	apiSecret: '',
};

export const AddDevAppDialog: FC<TAddDevAppDialogProps> = ({ open, onClose, onSuccess }) => {
	const dispatch = useDispatch();
	const loading = useSelector(getAddShopifyAppLoader);
	const [values, setValues] = useState<TFormValues>(EMPTY_VALUES);
	const [submitted, setSubmitted] = useState(false);

	useEffect(() => {
		if (!open) return;
		setValues(EMPTY_VALUES);
		setSubmitted(false);
	}, [open]);

	const setValue = (name: keyof TFormValues) => (value: string) =>
		setValues((prev) => ({ ...prev, [name]: value }));

	const isEmpty = (name: keyof TFormValues) => submitted && values[name].trim() === '';

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault();
		setSubmitted(true);
		if (Object.values(values).some((value) => value.trim() === '')) return;

		dispatch(
			addShopifyAppRequest({
				name: values.name.trim(),
				apiKey: values.apiKey.trim(),
				apiSecret: values.apiSecret.trim(),
				onSuccess: () => {
					onClose();
					onSuccess();
				},
			}),
		);
	};

	return (
		<Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ color: COLORS.PRIMARY, fontWeight: 'bold' }}>Add New Dev App</DialogTitle>
			<form onSubmit={handleSubmit} noValidate autoComplete="off">
				<DialogContent>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
						<Typography>
							Before adding the Developer Application, please create it by following{' '}
							<Link href={CREATE_APP_HELP_URL} target="_blank" rel="noopener noreferrer">
								Creating the EAS App in Shopify
							</Link>
							. Once created, copy the <b>Client ID</b> from the Shopify Dev Dashboard into the{' '}
							<b>Shopify API Key</b> field, and copy the <b>Client Secret</b> into the{' '}
							<b>Shopify API Secret</b> field.
						</Typography>
						<TextField
							label="App Name"
							value={values.name}
							onChange={(event) => setValue('name')(event.target.value)}
							required
							autoFocus
							error={isEmpty('name')}
							helperText={isEmpty('name') ? 'Enter the app name' : ''}
							fullWidth
						/>
						<TextField
							label="Shopify API Key"
							value={values.apiKey}
							onChange={(event) => setValue('apiKey')(event.target.value)}
							required
							error={isEmpty('apiKey')}
							helperText={isEmpty('apiKey') ? 'Enter the API key' : ''}
							fullWidth
						/>
						<TextField
							label="Shopify API Secret"
							type="password"
							value={values.apiSecret}
							onChange={(event) => setValue('apiSecret')(event.target.value)}
							required
							error={isEmpty('apiSecret')}
							helperText={isEmpty('apiSecret') ? 'Enter the API secret' : ''}
							inputProps={{ autoComplete: 'new-password' }}
							fullWidth
						/>
					</Box>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
					<Button variant="outlined" onClick={onClose} disabled={loading}>
						Cancel
					</Button>
					<Button variant="contained" type="submit" disabled={loading}>
						{loading ? <CircularProgress size={20} /> : 'Add'}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
